// Forms · Select
//
// Two-variant select: "pills" renders options as a horizontal row of clickable
// Pill chips; "dropdown" wraps AntD Select with our theme tokens.
//
// Same value / onChange / options API in both variants. Single- or multi-select
// via the `multiple` flag.
//
// All visual values come from `@ds/tokens/palette`. No raw `<div style>` or
// `<span style>` in this file — pills variant composes Pill + Inline;
// dropdown variant uses AntD Select (not audited as a raw HTML element).

import React, { useRef, useEffect } from "react";
import type { ReactNode } from "react";
import { Select as AntSelect } from "antd";
import type { SelectProps as AntSelectProps } from "antd";
import { Pill } from "@ds/display/Pill";
import { Inline } from "@ds/layout/Inline";
import type { ToneName } from "@ds/tokens/palette";

// ─── Closed enums ────────────────────────────────────────────────────────────

export type SelectVariant = "pills" | "dropdown";
export type SelectSize    = "sm" | "md" | "lg";

// ─── Option shape ────────────────────────────────────────────────────────────

export interface SelectOption<T extends string | number = string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  /**
   * Optional tone — pills variant + selected: overrides the default selected
   * tone ("primary" / "blue"). Ignored in dropdown variant.
   */
  tone?: ToneName;
  disabled?: boolean;
}

// ─── Inputs (data props — the model) ─────────────────────────────────────────

export interface SelectInput<T extends string | number = string> {
  value?: T | T[];
  options: SelectOption<T>[];
  /**
   * Rendering variant.
   * - `"pills"` (default "dropdown") — render as horizontal Inline of clickable Pill chips.
   * - `"dropdown"` — wrap AntD Select.
   */
  variant?: SelectVariant;
  /** Multi-select. Default false. */
  multiple?: boolean;
  /**
   * Free-entry tags mode (dropdown variant → AntD `mode="tags"`). Lets the user
   * type values that aren't in `options` and commit them as chips. Implies
   * multi-value. Takes precedence over `multiple` when both are set.
   * Ignored in pills variant.
   */
  tags?: boolean;
  /**
   * Closed-enum size. Pills default to "md". Dropdown: when unset, inherits
   * the surrounding AntD `ConfigProvider componentSize` (so a Select dropped
   * into the small-sized table toolbar matches the other controls); absent any
   * ConfigProvider that resolves to AntD's "middle".
   */
  size?: SelectSize;
  /** Render as disabled. Default false. */
  disabled?: boolean;
  /** Placeholder text (dropdown only). */
  placeholder?: string;
  /**
   * Allow items to wrap to multiple rows (pills variant only).
   * Default true.
   */
  wrap?: boolean;
  /** `data-testid` on the root element. */
  testId?: string;
  /**
   * DOM id forwarded to the underlying control (dropdown variant). AntD
   * `Form.Item` injects this for label association + scroll-to-error; keep it
   * so the DS Select is a faithful drop-in inside forms.
   */
  id?: string;
  /**
   * Auto-focus on mount (dropdown variant; for inline-edit).
   * Ignored in pills variant.
   */
  autoFocus?: boolean;
  /**
   * Force dropdown open on mount (dropdown variant; for inline-edit).
   * Ignored in pills variant.
   */
  open?: boolean;
  /**
   * Open the dropdown on mount but allow the user to close it (uncontrolled;
   * dropdown variant; for inline-edit). Maps to AntD `defaultOpen`.
   * Ignored in pills variant.
   */
  defaultOpen?: boolean;
  /**
   * Max tags rendered before collapsing into a "+N" affordance (multi-select
   * dropdown). e.g. `"responsive"`. Ignored in pills variant.
   */
  maxTagCount?: number | "responsive";
  /**
   * Validation status — passes through to AntD Select's status prop.
   * Ignored in pills variant.
   */
  status?: "" | "warning" | "error";
  /**
   * Allow clearing the selection (dropdown variant).
   * Ignored in pills variant.
   */
  allowClear?: boolean;
  /**
   * Enable type-to-filter (dropdown variant → AntD `showSearch`). Filtering
   * matches the option's STRING label (case-insensitive) — options whose label
   * is a non-string ReactNode aren't matched. Ignored in pills variant.
   */
  searchable?: boolean;
  /** Min width (px) for the dropdown control. Ignored in pills variant. */
  minWidth?: number;
}

// ─── Outputs (events emitted — controller hooks) ──────────────────────────────

export interface SelectOutput<T extends string | number = string> {
  onChange?: (value: T | T[] | undefined) => void;
  /**
   * Pass-through keyboard handler (Enter/Escape commit/cancel in inline-edit).
   * Ignored in pills variant.
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  /**
   * Pass-through blur handler (cancel-on-blur for inline-edit).
   * Ignored in pills variant.
   */
  onBlur?: () => void;
}

export type SelectProps<T extends string | number = string> = SelectInput<T> & SelectOutput<T>;

// ─── Size helpers ─────────────────────────────────────────────────────────────

const PILL_SIZE: Record<SelectSize, "sm" | "md"> = {
  sm: "sm",
  md: "md",
  lg: "md",
};

const ANT_SIZE: Record<SelectSize, AntSelectProps["size"]> = {
  sm:  "small",
  md:  "middle",
  lg:  "large",
};

// ─── Pills variant ────────────────────────────────────────────────────────────

function PillsSelect<T extends string | number>({
  value,
  options,
  multiple = false,
  size     = "md",
  disabled = false,
  wrap     = true,
  testId,
  onChange,
}: SelectProps<T>) {
  const selected = value === undefined
    ? []
    : Array.isArray(value) ? value : [value];

  function toggle(optionValue: T, isDisabled?: boolean) {
    if (disabled || isDisabled) return;
    if (multiple) {
      const next = selected.includes(optionValue)
        ? selected.filter((v) => v !== optionValue)
        : [...selected, optionValue];
      onChange?.(next);
    } else {
      const next = selected.includes(optionValue) ? undefined : optionValue;
      onChange?.(next);
    }
  }

  const pillSize = PILL_SIZE[size];

  return (
    <Inline gap="xs" wrap={wrap} testId={testId} data-testid={testId}>
      {options.map((opt) => {
        const isSelected = selected.includes(opt.value);
        const isDisabled = disabled || opt.disabled;
        const tone: ToneName = isSelected
          ? (opt.tone ?? "blue")
          : "grey";

        return (
          <Pill
            key={String(opt.value)}
            tone={tone}
            size={pillSize}
            icon={opt.icon}
            variant={isSelected ? "soft" : "outline"}
            testId={`select-option-${String(opt.value)}`}
            onClick={isDisabled ? undefined : () => toggle(opt.value, opt.disabled)}
          >
            {opt.label}
          </Pill>
        );
      })}
    </Inline>
  );
}

// ─── Dropdown variant ─────────────────────────────────────────────────────────

function DropdownSelect<T extends string | number>({
  value,
  options,
  multiple   = false,
  tags       = false,
  size,
  disabled   = false,
  placeholder,
  testId,
  id,
  autoFocus,
  open,
  defaultOpen,
  maxTagCount,
  status,
  allowClear,
  searchable,
  minWidth,
  onChange,
  onKeyDown,
  onBlur,
}: SelectProps<T>) {
  // Wrap each option label in a span carrying `data-testid="select-option-{value}"`
  // so e2e specs can target options by stable value (escapes the AntD
  // rc-virtual-list viewport check + locale-dependent label match). Keep the
  // raw string label as `searchText` so `showSearch` can type-to-filter (the
  // wrapped ReactNode label is otherwise unmatchable).
  const antOptions = options.map((opt) => ({
    value:    opt.value,
    label:    (
      <span data-testid={`select-option-${String(opt.value)}`}>{opt.label}</span>
    ),
    searchText: typeof opt.label === "string" ? opt.label : undefined,
    disabled: opt.disabled,
  }));

  // Filter on the preserved string label, not the ReactNode `label`.
  const filterOption: AntSelectProps["filterOption"] = searchable
    ? (input, option) =>
        ((option as { searchText?: string } | undefined)?.searchText ?? "")
          .toLowerCase()
          .includes(input.toLowerCase())
    : undefined;

  const rootStyle = minWidth ? { minWidth, width: "100%" } : { width: "100%" };

  // The AntD type-to-filter <input> is owned by rc-select and takes no testid
  // prop. Stamp `${testId}-search` on it via a ref (the wrapper uses
  // `display: contents` so it generates no box and never affects layout), so
  // specs fill the search deterministically instead of getByRole("combobox").
  const searchRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!testId || !searchable || !searchRef.current) return;
    const input = searchRef.current.querySelector(".ant-select-selection-search-input");
    if (input && !input.getAttribute("data-testid")) {
      input.setAttribute("data-testid", `${testId}-search`);
    }
  });

  function handleChange(v: T | T[]) {
    onChange?.(v);
  }

  if (multiple || tags) {
    return (
      <span ref={searchRef} className="ds-display-contents">
      <AntSelect<T[]>
        mode={tags ? "tags" : "multiple"}
        value={Array.isArray(value) ? value : value !== undefined ? [value] : undefined}
        options={antOptions as { value: T; label: ReactNode; disabled?: boolean }[]}
        size={size ? ANT_SIZE[size] : undefined}
        disabled={disabled}
        placeholder={placeholder}
        autoFocus={autoFocus}
        open={open}
        defaultOpen={defaultOpen}
        maxTagCount={maxTagCount}
        status={status}
        allowClear={allowClear}
        showSearch={searchable}
        filterOption={filterOption}
        onChange={(v) => onChange?.(v)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        id={id}
        style={rootStyle}
        data-testid={testId}
      />
      </span>
    );
  }

  return (
    <span ref={searchRef} className="ds-display-contents">
    <AntSelect<T>
      value={Array.isArray(value) ? value[0] : value}
      options={antOptions as { value: T; label: ReactNode; disabled?: boolean }[]}
      size={size ? ANT_SIZE[size] : undefined}
      disabled={disabled}
      placeholder={placeholder}
      autoFocus={autoFocus}
      open={open}
      defaultOpen={defaultOpen}
      status={status}
      allowClear={allowClear}
      showSearch={searchable}
      filterOption={filterOption}
      onChange={(v) => handleChange(v)}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      id={id}
      style={rootStyle}
      data-testid={testId}
    />
    </span>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

export function Select<T extends string | number = string>(props: SelectProps<T>) {
  const { variant = "dropdown" } = props;
  if (variant === "pills") return <PillsSelect<T> {...props} />;
  return <DropdownSelect<T> {...props} />;
}
